import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';

export interface ToggleButtonProps extends HTMLAttributes<HTMLInputElement> {
    checked?: boolean,
    disabled?: boolean,
    noSlider?: boolean,
}

const ToggleButtonWithForwardRef = React.forwardRef<HTMLInputElement, ToggleButtonProps>((props, ref) => {
    const [isOn, setIsOn] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const buttonClicked = useCallback((e: any) => {
        inputRef?.current?.click();
        e.stopPropagation(); //need this to stop clicking the input from propagating up and clicking parent click handlers.
    }, [inputRef]);

    let { noSlider, ...trimmedProps } = props;
    delete trimmedProps.className;

    useEffect(() => {
        if (!inputRef)
            return;

        if (ref == null) {
            if (props.defaultChecked != null) {
                setIsOn(props.defaultChecked);
            }
            return;
        }

        if (typeof ref === 'function') {
            ref(inputRef.current);
            if (inputRef && inputRef.current)
                setIsOn(inputRef.current.checked);
        }
        else
            if (ref !== null)
                ref.current = inputRef.current;
    }, [inputRef, ref, props.defaultChecked]);

    return (
        noSlider ?
            <span className={classNames(props.className, "toggle-button")}>
                <span className={classNames(props.defaultChecked ? "is-toggled-on-no-slider" : "is-toggled-off-no-slider")}></span>
            </span>
            : <span className={classNames(props.className, "toggle-button", { "cursor-pointer": !props.disabled, "cursor-not-allowed": props.disabled, "opacity-50": props.disabled })}>
                <input ref={inputRef} type="checkbox" className="visually-hidden" {...trimmedProps} />
                <span className={classNames(isOn ? "is-toggled-on" : "is-toggled-off")} onClick={buttonClicked}></span>
            </span>
    )
});
ToggleButtonWithForwardRef.displayName = 'ToggleButton';

ToggleButtonWithForwardRef.propTypes = {
    className: PropTypes.string,
    defaultChecked: PropTypes.bool,
    disabled: PropTypes.bool,
    noSlider: PropTypes.bool,
}

export const ToggleButton = React.memo(ToggleButtonWithForwardRef);
